import { List } from 'lucide-react';
import { cn } from '../lib/utils';
import { useWorkflowBar } from '../contexts/WorkflowBarContext';
import { getOrgType, getUserOrganizationName } from '../authUtils';

const STEP_META = {
  ACKNOWLEDGE: { width: 122, color: 'text-amber-700' },
  PROVIDE_INITIAL: { width: 124, color: 'text-sky-700' },
  UNRESOLVED: { width: 124, color: 'text-teal-700' },
  ONGOING_MONITOR: { width: 122, color: 'text-gray-900' },
};

const CLIP_FIRST = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%)';
const CLIP_MID = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%, 10px 50%)';
const CLIP_LAST = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 10px 50%)';

const BORDER_COLOR = 'hsl(220 13% 86%)';

function WorkflowStatusBar() {
  const {
    counts, overdueCounts, totalReferrals, visible,
    assignedToFilter, setAssignedToFilter,
    workflowStepFilter, setWorkflowStepFilter,
    qsActive, setQsActive,
    stepLabels,
    isClosedFilter, setIsClosedFilter,
  } = useWorkflowBar();

  if (!visible) return null;

  const totalOverdue = Object.values(overdueCounts).reduce((a, b) => a + b, 0);
  const allActive = workflowStepFilter === null;
  const orgType = getOrgType();
  const externalOrgLabel = orgType === 'TPA' || orgType === 'CARRIER'
    ? getUserOrganizationName()
    : null;
  // Selecting any active-list filter exits the Closed mode (Closed is its own
  // mutually-exclusive dimension layered on top of the active-list filters).
  const handleAssignedToClick = (key) => {
    setAssignedToFilter(key);
    setIsClosedFilter(false);
  };
  const handleWorkflowAllClick = () => {
    setWorkflowStepFilter(null);
    setIsClosedFilter(false);
  };
  const handleStepClick = (key, isActive) => {
    setWorkflowStepFilter(isActive ? null : key);
    setIsClosedFilter(false);
  };
  const handleQsClick = () => {
    setQsActive(prev => !prev);
    setIsClosedFilter(false);
  };
  // Closed is a separate mode: entering it clears the active-list filters
  // (the closed endpoint doesn't take a workflow step / open-questions filter).
  const handleClosedClick = () => {
    if (isClosedFilter) {
      setIsClosedFilter(false);
      return;
    }
    setIsClosedFilter(true);
    setWorkflowStepFilter(null);
    setAssignedToFilter('all');
    setQsActive(false);
  };

  // Arrow steps exclude OPEN_QUESTIONS — it is represented by the Qs toggle button
  const arrowSteps = stepLabels.filter(step => step.key !== 'OPEN_QUESTIONS');

  return (
    <div data-testid="workflow-status-bar" className="workflow-status-bar">
      {/* Assigned To filter group */}
      <div
        className="assigned-to-filter-group"
        style={{ borderColor: BORDER_COLOR }}
      >
        {[
          { key: 'all', label: 'All', width: 'w-[70px]', icon: true },
          { key: 'tpa_carrier', label: externalOrgLabel || 'TPA/Carrier', width: 'min-w-[80px] max-w-[140px] px-3' },
          { key: 'smartlight', label: 'SmartLight', width: 'w-[80px]' },
        ].map((item, i) => {
          const isItemActive = !isClosedFilter && assignedToFilter === item.key;
          return (
            <button
              key={item.key}
              type="button"
              className={cn(
                'assigned-to-filter-btn',
                item.width,
                i > 0 && 'assigned-to-filter-btn--bordered',
                isItemActive
                  ? 'assigned-to-filter-btn--active'
                  : 'assigned-to-filter-btn--inactive'
              )}
              style={{ borderColor: i > 0 ? BORDER_COLOR : undefined }}
              onClick={() => handleAssignedToClick(item.key)}
            >
              {item.icon && <List className="h-3 w-3 shrink-0" />}
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Workflow step arrow buttons */}
      <div className="workflow-step-buttons">
        {/* All */}
        <button
          type="button"
          className={cn(
            'workflow-all-btn',
            (allActive && !isClosedFilter) ? 'workflow-all-btn--active' : 'workflow-all-btn--inactive'
          )}
          style={{ zIndex: (allActive && !isClosedFilter) ? 10 : 6, borderColor: BORDER_COLOR }}
          onClick={handleWorkflowAllClick}
        >
          <div className="workflow-all-btn__label-row">
            <List className="h-3.5 w-3.5" />
            <span>All</span>
          </div>
          <div className="workflow-all-btn__count-row">
            <span className={cn(
              'workflow-count-badge',
              (allActive && !isClosedFilter) ? 'workflow-count-badge--active' : 'workflow-count-badge--inactive'
            )}>
              {totalReferrals}
            </span>
            {totalOverdue > 0 && (
              <span className="workflow-overdue-badge">{totalOverdue}</span>
            )}
          </div>
        </button>

        {/* Arrow steps — OPEN_QUESTIONS excluded — plus the Closed filter,
            which is its own mode rather than a workflow step. */}
        {(() => {
          const arrowItems = [
            ...arrowSteps.map((s) => ({ ...s, isClosed: false })),
            { key: 'CLOSED', label: 'Closed', isClosed: true },
          ];
          return arrowItems.map((item, i, arr) => {
            const isFirst = i === 0;
            const isLast = i === arr.length - 1;
            const isActive = item.isClosed
              ? isClosedFilter
              : (!isClosedFilter && workflowStepFilter === item.key);
            const clipPath = isFirst ? CLIP_FIRST : isLast ? CLIP_LAST : CLIP_MID;
            const count = counts[item.key] ?? 0;
            const overdue = overdueCounts[item.key] ?? 0;
            const meta = STEP_META[item.key] ?? { width: 115, color: 'text-foreground' };
            return (
              <div
                key={item.key}
                className={cn(
                  'workflow-arrow-step',
                  // The Closed button sits flush (no overlap) so it reads as a
                  // separate dimension from the workflow steps.
                  !isFirst && !item.isClosed && 'workflow-arrow-step--overlap'
                )}
                style={{
                  width: `${meta.width}px`,
                  height: '68px',
                  zIndex: isActive ? 10 : i + 2,
                }}
              >
                {/* Border layer */}
                <div
                  className={cn('absolute inset-0', isLast && 'rounded-r-2xl')}
                  style={{ clipPath, backgroundColor: BORDER_COLOR }}
                />
                {/* Button inset 1px to reveal border layer */}
                <button
                  type="button"
                  data-step={item.key}
                  className={cn(
                    'workflow-arrow-btn',
                    item.isClosed && 'workflow-arrow-btn--closed',
                    isLast && 'workflow-arrow-btn--last',
                    isActive ? 'workflow-arrow-btn--active' : 'workflow-arrow-btn--inactive'
                  )}
                  style={{
                    clipPath,
                    margin: '1px',
                    width: 'calc(100% - 2px)',
                    height: 'calc(100% - 2px)',
                  }}
                  onClick={() => item.isClosed ? handleClosedClick() : handleStepClick(item.key, isActive)}
                >
                  {item.isClosed ? (
                    <div className={cn(
                      'workflow-arrow-btn__label-row',
                      'workflow-arrow-btn__label-row--mid-last',
                      !isActive && meta.color
                    )}>
                      {item.label}
                    </div>
                  ) : (
                    <>
                      <div className={cn(
                        'workflow-arrow-btn__label-row',
                        isFirst
                          ? 'workflow-arrow-btn__label-row--first'
                          : 'workflow-arrow-btn__label-row--mid-last',
                        !isActive && meta.color
                      )}>
                        {item.label.includes('/') ? (
                          <span className="text-center">{item.label.split('/')[0]}/<br />{item.label.split('/')[1]}</span>
                        ) : item.label}
                      </div>
                      <div className="workflow-arrow-btn__count-row">
                        <span
                          data-testid="step-count"
                          className={cn(
                            'workflow-count-badge',
                            isActive ? 'workflow-count-badge--active' : 'workflow-count-badge--inactive'
                          )}
                        >
                          {count}
                        </span>
                        {overdue > 0 && (
                          <span className="workflow-overdue-badge">{overdue}</span>
                        )}
                      </div>
                    </>
                  )}
                </button>
              </div>
            );
          });
        })()}
      </div>

      {/* Qs to TPA/Carrier toggle button */}
      <button
        type="button"
        data-testid="qs-to-tpa-carrier-btn"
        className={cn(
          'workflow-qs-toggle-btn',
          qsActive ? 'workflow-qs-toggle-btn--active' : 'workflow-qs-toggle-btn--inactive'
        )}
        style={{ borderColor: !qsActive ? BORDER_COLOR : undefined }}
        onClick={handleQsClick}
      >
        <span>
          {externalOrgLabel ? `Qs to ${externalOrgLabel}` : 'Qs to TPA/Carrier'}
        </span>
      </button>

    </div>
  );
}

export { WorkflowStatusBar };
